import { Box, Link as MuiLink, Skeleton, Typography, useTheme } from '@mui/material';
import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { PageHeaderAction, PageHeaderBreadcrumb } from './types';
import { Breadcrumbs } from '../../mui-wrapper-components/Breadcrumbs/Breadcrumbs';
import { Button as ButtonV2 } from '../../mui-wrapper-components/Button/Button';
import { Icon as IconV2 } from '../../mui-wrapper-components/Icon/Icon';
import { Tooltip } from '../../mui-wrapper-components/Tooltip/Tooltip';
import { PageTabProps } from '../PageTabs/PageTab';
import PageTabs from '../PageTabs/PageTabs';
import { StatusDisplay } from '../StatusDisplay/StatusDisplay';

export type PageHeaderProps = {
  /**
   * @prop {string} - The component ID, is used as part of test id's
   */
  id: string;

  /**
   * @prop {boolean} - breadcrumb leading icon
   * @default false
   */
  breadcrumbLeadingIcon?: boolean;

  /**
   * @prop {PageHeaderBreadcrumb[]} - The breadcrumb link definitions
   */
  breadcrumbs?: PageHeaderBreadcrumb[];

  /**
   * @prop {PageHeaderAction[]} - The action button definitions
   */
  actions?: PageHeaderAction[];

  /**
   * @prop {string} - The page title
   */
  pageTitle: string;

  /**
   * @prop {string} - The page title
   */
  pageTitleIcon?: ReactNode;

  /**
   * @prop {ReactNode} - Placeholder for adding a status component or information bar
   */
  infoBarContainer?: ReactNode;

  /**
   * @prop {string} - The status type for header display
   */
  status?: string;

  /**
   * @prop {PageTabProps[]} - The page tabs definitions
   */
  pageTabs?: PageTabProps[];

  /**
   * @prop {ReactNode} - Content to display to the right of the tabs (e.g., unsaved changes indicator)
   */
  tabsRightContent?: ReactNode;

  /**
   * @prop {PageTabOnChange} - The function to call when the tab is clicked
   */
  onPageTabChange?: (e: React.SyntheticEvent, tab: string) => void;

  /**
   * @prop {React.ReactNode} - The page description
   */
  description?: React.ReactNode;

  /**
   * @prop {string} - Title below the pageTabs
   */
  titleBelowTabs?: string;

  /**
   * @prop {React.ReactNode} - Description below the pageTabs
   */
  descriptionBelowTabs?: React.ReactNode;
};

// TODO: @art.kulakow - Cleanup this component
export const PageHeader = ({
  id,
  breadcrumbLeadingIcon,
  breadcrumbs,
  actions,
  pageTitle,
  pageTitleIcon,
  status,
  pageTabs,
  onPageTabChange,
  description,
  infoBarContainer,
  titleBelowTabs,
  descriptionBelowTabs,
  tabsRightContent,
}: PageHeaderProps) => {
  const theme = useTheme();

  const loadBreadcrumbsAndActions = () => {
    if (!breadcrumbs && !actions) return null;

    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
        data-testid="pageHeader-breadcrumbs"
      >
        <Box sx={{ display: 'flex', flex: 2, alignItems: 'start' }}>
          {breadcrumbs && (
            <>
              {breadcrumbLeadingIcon && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start',
                    height: `${theme.spacing(4)}`,
                    marginRight: `${theme.spacing(2)}`,
                    mt: `${theme.spacing(1)}`,
                  }}
                >
                  <IconV2
                    id={`pageHeader-breadcrumb-leading-icon-${id}`}
                    icon={'arrowLeft'}
                    width={16}
                    height={16}
                  />
                </Box>
              )}
              <Breadcrumbs
                id={`pageHeader-breadcrumbs-${id}`}
                separator={'/'}
                sx={{ color: 'text.secondary', mb: ` ${theme.spacing(2)}` }}
              >
                {breadcrumbs.map(({ label, onClick, href, isLoading }, index) => {
                  const isLast = breadcrumbs.length !== 1 && index === breadcrumbs.length - 1;
                  const commonStyles = {
                    fontWeight: '400',
                    cursor: isLast ? 'default' : 'pointer',
                    color: isLast
                      ? `${theme.palette.text.primary}`
                      : `${theme.palette.text.secondary}`,
                    textDecoration: 'none',
                  };

                  // Handle onClick with modifier key support for Ctrl+click and Shift+click
                  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                    // If Ctrl/Cmd or Shift is pressed, allow default browser behavior (open in new tab/window)
                    if (e.ctrlKey || e.metaKey || e.shiftKey) {
                      if (href) {
                        // If href is provided, let the browser handle it naturally
                        return;
                      }
                      // If no href but onClick exists, prevent default to avoid navigation
                      e.preventDefault();
                      return;
                    }
                    // Normal click - call onClick handler
                    if (onClick) {
                      e.preventDefault();
                      onClick(e);
                    }
                  };

                  // Use react-router-dom Link when href is provided (enables Ctrl+click and Shift+click naturally)
                  if (href) {
                    return (
                      <MuiLink
                        key={label}
                        component={RouterLink}
                        to={href}
                        underline={isLast ? 'none' : 'hover'}
                        typography={'body1'}
                        onClick={handleClick}
                        data-testid={`pageHeader-breadcrumbs-link-${index + 1}-${id}`}
                        sx={commonStyles}
                      >
                        {isLoading ? <Skeleton variant="text" width={100} height={24} /> : label}
                      </MuiLink>
                    );
                  }

                  // Fallback to MUI Link with onClick handler
                  return (
                    <MuiLink
                      key={label}
                      underline={isLast ? 'none' : 'hover'}
                      typography={'body1'}
                      onClick={handleClick}
                      data-testid={`pageHeader-breadcrumbs-link-${index + 1}-${id}`}
                      sx={commonStyles}
                    >
                      {isLoading ? <Skeleton variant="text" width={100} height={24} /> : label}
                    </MuiLink>
                  );
                })}
              </Breadcrumbs>
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box id={id} data-testid={id}>
      <Box
        data-testid="pageHeader-container"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          py: `${theme.spacing(4)}`,
          width: '100%',
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'start',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {loadBreadcrumbsAndActions()}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {pageTitleIcon && (
                  <Box
                    sx={{
                      width: `${theme.spacing(11)}`,
                      height: `${theme.spacing(9)}`,
                      marginRight: `${theme.spacing(1)}`,
                    }}
                  >
                    {pageTitleIcon}
                  </Box>
                )}
                <Typography data-testid={`${id}-title`} variant="h4" sx={{ flex: 3 }}>
                  {pageTitle}
                </Typography>
              </Box>
            </Box>
            <Box>
              {actions && (
                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    gap: `${theme.spacing(4)}`,
                  }}
                >
                  {actions.map(
                    ({
                      id,
                      label,
                      btnType,
                      disabled,
                      startIcon,
                      onClickHandler,
                      customRenderer,
                      waitButton,
                      tooltipText,
                    }) => {
                      if (customRenderer) {
                        return customRenderer;
                      }

                      return (
                        <Tooltip
                          key={label}
                          id={`${id}-tooltip`}
                          title={tooltipText}
                          placement="left"
                          width="auto"
                          arrow
                        >
                          {/* span is needed to display tooltip when button is disabled */}
                          <span>
                            <ButtonV2
                              id={id}
                              displayLabel={label}
                              data-testid={`pageHeader-action-button-${label}`}
                              btnType={btnType}
                              disabled={disabled}
                              startIcon={startIcon}
                              onClickHandler={onClickHandler}
                              waitButton={waitButton}
                            />
                          </span>
                        </Tooltip>
                      );
                    }
                  )}
                </Box>
              )}
            </Box>
          </Box>
          {status && (
            <Box sx={{ marginTop: `${theme.spacing(2)}` }}>{<StatusDisplay status={status} />}</Box>
          )}
          {infoBarContainer && <Box>{infoBarContainer}</Box>}
        </Box>
        <Box sx={{ width: '100%' }}>
          {description && (
            <Box sx={{ marginTop: `${theme.spacing(4)}` }}>
              <Typography
                variant="body1"
                color={`${theme.palette.text.primary}`}
                sx={{ width: '75%' }}
              >
                {description}
              </Typography>
            </Box>
          )}
          {pageTabs && (
            <PageTabs
              tabs={pageTabs}
              onChange={onPageTabChange}
              id={'pageHeader-tabs'}
              rightContent={tabsRightContent}
            />
          )}
          {titleBelowTabs && (
            <Box sx={{ mt: `${theme.spacing(6)}` }}>
              <Typography variant="h5">{titleBelowTabs}</Typography>
            </Box>
          )}
          {descriptionBelowTabs && (
            <Box sx={{ mt: `${theme.spacing(4)}` }}>
              <Typography
                variant="body1"
                color={`${theme.palette.text.primary}`}
                sx={{ width: '75%' }}
              >
                {descriptionBelowTabs}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
